import { ID, TaskCalendarOverride } from '@griply/types';
import {
  getTaskCalendarSyncCandidates,
  GetTaskCalendarSyncCandidatesOptions,
  LinkedItemGoal,
} from '@griply/utils';
import { useGoals } from 'features/goals';
import { useCallback } from 'react';
import { useSetTaskCalendarOverrideMutation } from 'shared/hooks/queries/use-set-task-calendar-override-mutation';

import { useExternalCalendarConnections } from './use-external-calendar-connections';

export const useSetTaskCalendarOverride = () => {
  const { mutate } = useSetTaskCalendarOverrideMutation();
  const connections = useExternalCalendarConnections();
  const goals = useGoals();

  return useCallback(
    (
      task: GetTaskCalendarSyncCandidatesOptions['task'],
      patch: Partial<TaskCalendarOverride>,
    ) => {
      const goalsById = new Map<ID, LinkedItemGoal>(
        goals.map((goal) => [goal.id, goal]),
      );
      // Reuses the candidate resolution rather than re-deriving which connections
      // cover this task — that copy missed the all-tasks target and silently
      // dropped every override for tasks synced only through it.
      const candidateConnectionIds = new Set(
        getTaskCalendarSyncCandidates({ task, connections, goalsById }).map(
          ({ connectionId }) => connectionId,
        ),
      );

      connections.forEach((connection) => {
        if (!candidateConnectionIds.has(connection.id)) {
          return;
        }

        mutate({
          connectionId: connection.id,
          taskId: task.id,
          current: connection.settings?.taskOverrides?.[task.id] ?? {},
          patch,
        });
      });
    },
    [mutate, connections, goals],
  );
};
